import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SubscriptionDto } from '@/Domain/api/Subscription/dto/Subscription.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { CancelSubscriptionDialog } from '@/Domain/components/Subscription/CancelSubscription.dialog';
import { SubscriptionHelper } from '@/Domain/helper/Subscription.helper';
import { useChangeSubscriptionStatus } from '@/Domain/hooks/Subscription/useChangeSubscriptionStatus.hook';

type Props = {
  currentPlan: SubscriptionDto['current_plan'];
  canceled?: boolean;
  onOpenChange: () => void;
  isOpen: boolean;
};

export const DowngradeToTierPlanDialog = ({
  isOpen,
  onOpenChange,
  currentPlan,
  canceled,
}: Props) => {
  const { t } = useTranslation('subscription_page');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const { handleChangeSubscriptionStatus } = useChangeSubscriptionStatus({
    cancelCallback: () => {
      setIsCancelDialogOpen(false);
      onOpenChange();
    },
    activeCallback: () => {},
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('change_plan_to_trial_dialog.title')}</DialogTitle>
          <DialogDescription className="sr-only" />
        </DialogHeader>
        <p className="text-sm text-foreground-subtle">
          <Trans
            t={t}
            i18nKey="change_plan_to_trial_dialog.description"
            components={{
              tier_label: (
                <span className="text-foreground font-medium">
                  {SubscriptionHelper.getTrierLabel(currentPlan.tier, undefined, canceled)}{' '}
                  {SubscriptionHelper.getIntervalLabel(currentPlan.interval)}
                </span>
              ),
            }}
          />
        </p>

        <Separator />
        <DialogFooter>
          <Button variant="danger" onClick={() => setIsCancelDialogOpen(true)}>
            {t('change_plan_to_trial_dialog.button.cancel_subscription')}
          </Button>
          <DialogClose>
            <Button variant="dark">{t('change_plan_dialog.button.cancel')}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>

      <CancelSubscriptionDialog
        isOpen={isCancelDialogOpen}
        onOpenChange={() => setIsCancelDialogOpen((prev) => !prev)}
        onCallback={() => handleChangeSubscriptionStatus('cancel')}
      />
    </Dialog>
  );
};
